import { Badge, Card, Divider, Group, Stack, Text, Title } from "@mantine/core";
import type { PartPaymentSplit } from "@safiri/shared";
import { formatDateTime, formatMoney } from "../../lib/format";

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Stack gap={0} align="flex-end">
        <Text size="sm" fw={500}>{value}</Text>
        {sub && <Text size="xs" c="dimmed">{sub}</Text>}
      </Stack>
    </Group>
  );
}

/**
 * Trip detail → part wallet / part cash split and the state of each part
 * (docs/part-wallet-payment-feature-spec.md §4).
 */
export function PartPaymentCard({
  split,
  fare,
  closedUnpaid,
}: {
  split: PartPaymentSplit;
  fare?: number;
  closedUnpaid: boolean;
}) {
  const cashState = closedUnpaid
    ? { label: "Closed unpaid", color: "red" }
    : split.cashConfirmedAt
      ? { label: `Confirmed by ${split.cashConfirmedBy ?? "driver"}`, color: "teal" }
      : { label: "Awaiting driver", color: "yellow" };

  return (
    <Card withBorder radius="md" padding="lg" mt="md">
      <Group justify="space-between" mb="md">
        <Title order={5}>Wallet + cash</Title>
        <Badge color={cashState.color} variant="light">{cashState.label}</Badge>
      </Group>
      <Stack gap="xs">
        <Row label="Fare" value={formatMoney(fare)} />
        <Divider />
        <Row
          label="From wallet"
          value={formatMoney(split.walletAmount)}
          sub={
            split.walletRefundedAt
              ? `Refunded ${formatDateTime(String(split.walletRefundedAt))}`
              : split.walletDebitedAt
                ? `Debited ${formatDateTime(String(split.walletDebitedAt))}`
                : undefined
          }
        />
        {split.walletToCancellationFees > 0 && (
          <Row
            label="of which earlier cancellation fees"
            value={formatMoney(split.walletToCancellationFees)}
          />
        )}
        <Row
          label="Cash to driver"
          value={formatMoney(split.cashAmount)}
          sub={
            split.cashConfirmedAt
              ? `Confirmed ${formatDateTime(String(split.cashConfirmedAt))}`
              : undefined
          }
        />
        {split.walletRefundReason && (
          <Text size="xs" c="dimmed">Refund reason: {split.walletRefundReason}</Text>
        )}
      </Stack>
    </Card>
  );
}
